// ~/apps/themusicchurch-app/frontend/src/pages/Ministries.jsx

import React, { useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import MondayZoomLink from "../components/Common/MondayZoomLink";

const MinistriesWrapper = styled.div`
  min-height: 100vh;
  background: ${(props) => props.theme.colors.homeNavy};
  color: ${(props) => props.theme.colors.homeText};

  h1, h2, h3, h4, h5, h6 { color: ${(props) => props.theme.colors.homeGold}; }
  a:focus-visible, button:focus-visible, input:focus-visible, select:focus-visible, textarea:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.accent};
    outline-offset: 4px;
  }
`;

// Title section with minimal padding
const TitleSection = styled.section`
  padding: ${(props) => props.theme.spacing.xl}
    ${(props) => props.theme.spacing.lg};
  text-align: center;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: ${(props) => props.theme.colors.homeNavy};
  > :not([aria-hidden]) { position: relative; z-index: 1; }

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: ${(props) => props.theme.spacing.lg}
      ${(props) => props.theme.spacing.lg};
  }
`;

// Static, peripheral decoration keeps the central reading area quiet.
const GalacticAtmosphere = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse at 50% 50%, #071c2d 0%, rgba(7, 28, 45, .94) 30%, transparent 70%),
    radial-gradient(ellipse at 4% 10%, #165b63 0%, transparent 48%),
    radial-gradient(ellipse at 98% 85%, #164b66 0%, transparent 45%);
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    opacity: .85;
    background:
      radial-gradient(circle at 3% 24%, #e4c28c 0 1px, transparent 1.5px),
      radial-gradient(circle at 13% 12%, #c6d9e2 0 1px, transparent 1.5px),
      radial-gradient(circle at 19% 72%, #c6d9e2 0 .7px, transparent 1.2px),
      radial-gradient(circle at 6% 83%, #c6d9e2 0 1px, transparent 1.5px),
      radial-gradient(circle at 25% 8%, #c6d9e2 0 .7px, transparent 1.2px),
      radial-gradient(circle at 76% 11%, #c6d9e2 0 .7px, transparent 1.2px),
      radial-gradient(circle at 88% 38%, #e4c28c 0 1px, transparent 1.5px),
      radial-gradient(circle at 97% 17%, #c6d9e2 0 1px, transparent 1.5px),
      radial-gradient(circle at 81% 88%, #c6d9e2 0 .7px, transparent 1.2px),
      radial-gradient(circle at 94% 91%, #c6d9e2 0 1px, transparent 1.5px);
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
      radial-gradient(circle, rgba(228, 237, 241, .85) 0 1px, transparent 1.8px),
      radial-gradient(circle, rgba(127, 205, 205, .65) 0 .7px, transparent 1.4px),
      radial-gradient(circle, rgba(228, 194, 140, .65) 0 1.3px, transparent 2.4px),
      radial-gradient(circle, rgba(198, 217, 226, .4) 0 .5px, transparent 1px);
    background-size: 137px 113px, 83px 151px, 293px 197px, 59px 79px;
    background-position: 17px 21px, 43px 67px, 71px 37px, 9px 13px;
    mask-image: radial-gradient(ellipse at 50% 45%, transparent 20%, rgba(0,0,0,.2) 42%, #000 75%);
  }
  .star {
    width: 3px; height: 3px; background: #e4edf1;
    left: var(--x); top: var(--y);
    box-shadow: 0 0 7px 2px rgba(127, 205, 205, .45);
  }
  .star::before, .star::after {
    content: ""; position: absolute; background: linear-gradient(transparent, #c6d9e2, transparent);
    width: 1px; height: 13px; left: 1px; top: -5px;
  }
  .star::after { transform: rotate(90deg); }
  span {
    position: absolute;
    border-radius: 50%;
    box-shadow: 0 0 18px rgba(99, 194, 193, .08);
  }
  .teal-planet {
    width: clamp(22px, 3vw, 48px); aspect-ratio: 1;
    left: 6%; top: 18%; opacity: .55;
    background: radial-gradient(circle at 27% 24%, #70a5a5, #234f60 35%, #071c2d 72%);
  }
  .distant-moon {
    width: 9px; aspect-ratio: 1; right: 17%; top: 13%; opacity: .4;
    background: radial-gradient(circle at 25% 25%, #c6d9e2, #103a50 65%);
  }
  @media (max-width: 760px) {
    .teal-planet { left: 1%; top: 4%; opacity: .3; }
    .distant-moon { right: 5%; top: 3%; }
    &::before { opacity: .6; }
  }
`;

const PageTitle = styled(motion.h1)`
  font: 700 clamp(2.8rem, 6vw, 4.5rem)/1.05 "DIN Condensed", "Arial Narrow", sans-serif;
  text-transform: uppercase;
  letter-spacing: .03em;
  color: ${(props) => props.theme.colors.homeGold};
  width: fit-content;
  max-width: 100%;
  margin: 0 auto ${(props) => props.theme.spacing.sm};
`;

const SolarSubtitle = styled.div`
  position: relative;
  max-width: 100%;
  margin: 8px auto 0;
  padding: 52px 0;
  isolation: isolate;
  .orbit {
    position: absolute; inset: 8px 0; border-radius: 50%;
    border: 1px solid rgba(127, 205, 205, .22); pointer-events: none;
    z-index: -1;
  }
  .orbit.inner { inset: 24px 8%; border-color: rgba(228, 194, 140, .16); }
  .planet {
    position: absolute; width: var(--size, 12px); aspect-ratio: 1; border-radius: 50%;
    left: var(--x); top: var(--y); transform: translate(-50%, -50%);
    background: radial-gradient(circle at 30% 25%, #7fcdcd, #24495a 55%, #071c2d 95%);
    box-shadow: 0 0 14px rgba(127, 205, 205, .12);
    pointer-events: none;
  }
  .planet.warm { background: radial-gradient(circle at 30% 25%, #e4c28c, #786b52 50%, #071c2d 95%); }

`;

const Subtitle = styled(motion.p)`
  font-family: "Helvetica Neue", "Arial Narrow", Arial, sans-serif;
  font-weight: 200;
  font-size: clamp(1rem, 1.65vw, 1.375rem);
  line-height: 1.55;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.accent};
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow-wrap: anywhere;
  text-wrap: balance;
`;

const ContentSection = styled.section`
  padding: ${(props) => props.theme.spacing["3xl"]}
    ${(props) => props.theme.spacing.lg};
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    padding: ${(props) => props.theme.spacing["2xl"]}
      ${(props) => props.theme.spacing.lg};
  }
`;

const IntroText = styled(motion.div)`
  max-width: 900px;
  margin: 24px auto 0;
  text-align: center;
  font-size: clamp(1.1875rem, 1.05rem + .5vw, 1.375rem);
  line-height: 1.65;
  color: ${(props) => props.theme.colors.accent};
`;

const MinistriesGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${(props) => props.theme.spacing["2xl"]};
`;

const MinistryCard = styled(motion.div)`
  background: ${(props) => props.theme.colors.homeSurface};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${(props) => props.theme.shadows.lg};
  transition: all ${(props) => props.theme.transitions.base};

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${(props) => props.theme.shadows.xl};
  }
`;

const MinistryImage = styled.div`
  width: 100%;
  height: 250px;
  background: url(${(props) => props.image}) center/cover;
  ${(props) => props.image.includes("/home-preview/") && `background-size: contain; background-repeat: no-repeat;`}
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  }
`;

const MinistryContent = styled.div`
  padding: ${(props) => props.theme.spacing["2xl"]};
`;

const MinistryTitle = styled.h3`
  color: ${(props) => props.theme.colors.homeGold};
  font-size: ${(props) => props.theme.fontSizes["2xl"]};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const MinistryDescription = styled.p`
  color: ${(props) => props.theme.colors.homeText};
  line-height: 1.8;
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const LearnMoreLink = styled(Link)`
  color: ${(props) => props.theme.colors.accent};
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.homeGold};
    gap: ${(props) => props.theme.spacing.md};
  }

  &::after {
    content: "→";
    font-size: ${(props) => props.theme.fontSizes.xl};
  }
`;

const Ministries = () => {
  const titleRef = useRef(null);
  const [titleWidth, setTitleWidth] = useState(null);

  useLayoutEffect(() => {
    const title = titleRef.current;
    if (!title) return undefined;
    let active = true;
    const measure = () => {
      if (active) setTitleWidth(title.getBoundingClientRect().width);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(title);
    window.addEventListener("resize", measure);
    document.fonts?.ready.then(measure);
    document.fonts?.addEventListener("loadingdone", measure);
    return () => {
      active = false;
      observer.disconnect();
      window.removeEventListener("resize", measure);
      document.fonts?.removeEventListener("loadingdone", measure);
    };
  }, []);

  const ministries = [
    {
      number: 1,
      title: "The Millennium",
      description:
        "YouTube weekly show and podcast. Panel discussion on this present age and the age to come. The prophesy speaks of 1000 years of peace and productivity.",
      image: "/images/home-preview/millennium-framed-v1.png",
      link: "/ministries/the-millennium",
    },
    {
      number: 2,
      title: "The Caught-Up Club",
      description:
        "Audio/visual content creation for YouTube channel and all social media. A wide mix of extended play music featuring various styles, promoting the ways of God.",
      image: "/images/home-preview/caught-up-club-mirrored-recess-final.png",
      link: "/ministries/caught-up-club",
    },
    {
      number: 3,
      title: "Monday Musicians",
      description:
        "Zoom weekly group therapy sessions the day after church. The least are the greatest, sharing, caring, confessing, comforting, exhorting.",
      image: "/images/home-preview/monday-musicians-beverly-mic-proof-v113.png",
      link: "/ministries/monday-musicians",
    },
    {
      number: 4,
      title: "Care Music Tours",
      description:
        "Live music concert series. We use all music for ministry, to bring joy, healing and peace to those restricted by illness, age, disease, and imprisonment.",
      image: "/images/home-preview/care-paired-singers-v51.png",
      link: "/ministries/care-music-tours",
    },
    {
      number: 5,
      title: "Ministry Before Industry",
      description:
        "Our media production department. Artistry bows to anointing here, and every project is a testament to the truth that ministry leads the way.",
      image: "/images/home-preview/mbi-framed-v8.png",
      link: "/ministries/ministry-before-industry",
    },
    {
      number: 6,
      title: "Music Church Worship Academy",
      description:
        "Focuses on music education, its design, destiny and purpose. We study and respectfully admire this great art and science as a gift from God.",
      image: "/images/home-preview/worship-academy-framed-v3.png",
      link: "/ministries/worship-academy",
    },
    {
      number: 7,
      title: "Quiet Kindness",
      description:
        "All about quiet acts of service without seeking recognition. Dedicated to meeting the needs of individuals and communities in practical ways.",
      image: "/images/home-preview/quiet-kindness-framed-v6.png",
      link: "/ministries/quiet-kindness",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Our Ministries - The Music Church</title>
        <meta
          name="description"
          content="Discover the 7 ministries of The Music Church, each uniquely contributing to our mission of spreading faith, creativity, and service."
        />
      </Helmet>

      <MinistriesWrapper>
        {/* Ministry introduction with decorative orbital accents */}
        <TitleSection>
          <GalacticAtmosphere aria-hidden="true">
            <span className="star" style={{ "--x": "8%", "--y": "35%" }} />
            <span className="star" style={{ "--x": "22%", "--y": "9%" }} />
            <span className="star" style={{ "--x": "91%", "--y": "20%" }} />
            <span className="star" style={{ "--x": "86%", "--y": "78%" }} />
            <span className="star" style={{ "--x": "12%", "--y": "89%" }} />
            <span className="teal-planet" />
            <span className="distant-moon" />
          </GalacticAtmosphere>
          <PageTitle
            ref={titleRef}
            variants={titleVariants}
            initial="hidden"
            animate="visible"
          >
            Our Ministries
          </PageTitle>
          <SolarSubtitle style={{ width: titleWidth === null ? undefined : `${titleWidth + 200}px` }}>
            <span className="orbit" aria-hidden="true" />
            <span className="orbit inner" aria-hidden="true" />
            <span className="planet warm" aria-hidden="true" style={{ "--x": "18%", "--y": "15%", "--size": "12px" }} />
            <span className="planet" aria-hidden="true" style={{ "--x": "42%", "--y": "5%", "--size": "8px" }} />
            <span className="planet" aria-hidden="true" style={{ "--x": "73%", "--y": "13%", "--size": "20px" }} />
            <span className="planet warm" aria-hidden="true" style={{ "--x": "95%", "--y": "30%", "--size": "9px" }} />
            <span className="planet" aria-hidden="true" style={{ "--x": "48%", "--y": "95%", "--size": "10px" }} />
            <span className="planet warm" aria-hidden="true" style={{ "--x": "13%", "--y": "80%", "--size": "15px" }} />
          <Subtitle
            style={{ width: titleWidth === null ? undefined : `${titleWidth}px` }}
            variants={titleVariants}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
          >
            SEVEN HARMONIC CENTERS OF DIVINE RHYTHM
          </Subtitle>
          </SolarSubtitle>
          <IntroText
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p>
              At the heart of The Music Church, the 7 harmonic centers magnetize
              a divine rhythm, expressed through our 7 ministries flowing
              together. Each one is a pillar in the sanctuary of creativity,
              faith, and purpose, as we seek to do God's will individually and
              collectively.
            </p>
          </IntroText>
        </TitleSection>

        <ContentSection>


          <MinistriesGrid
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {ministries.map((ministry) => (
              <MinistryCard
                key={ministry.number}
                variants={cardVariants}
                whileHover={{ y: -8 }}
              >
                <Link
                  to={ministry.link}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <MinistryImage image={ministry.image} />
                  <MinistryContent>
                    <MinistryTitle>{ministry.title}</MinistryTitle>
                    <MinistryDescription>
                      {ministry.description}
                    </MinistryDescription>
                    <LearnMoreLink to={ministry.link}>Learn More</LearnMoreLink>
                  </MinistryContent>
                </Link>
                {ministry.link === "/ministries/monday-musicians" && (
                  <div style={{ padding: "0 24px 24px" }}>
                    <MondayZoomLink />
                  </div>
                )}
              </MinistryCard>
            ))}
          </MinistriesGrid>
        </ContentSection>
      </MinistriesWrapper>
    </>
  );
};

export default Ministries;
